import { Component, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { OpportunityStatusView } from '@api-proxies';
import { OpportunityStatusService } from '@http/clients/opportunity-status.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { AwaiterService } from '@services/awaiter.service';
import { CustomMenuItemComponent } from '@shared/custom-menu-item/custom-menu-item.component';
import { LoadingButtonComponent } from '@shared/loading-button/loading-button.component';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    GenericDropdownComponent,
    IconSanitizerService,
    InputFormatterDirective,
    ModalComponentBase,
    SnackbarNotificationService
} from 'ngx-lib';

export interface IModalManualStatusParameters {
    actualValue?: number;
    actualGrossMargin?: number;
    opportunityId: number;
    opportunityStatusId: number;
    opportunityManualId?: number;
}

export interface IManualStatusResponse {
    id?: number;
    actualValue?: number;
    actualGrossMargin?: number;
    opportunityStatusId?: number;
}

@Component({
    selector: 'app-modal-manual-status',
    templateUrl: './modal-manual-status.component.html',
    styleUrls: ['./modal-manual-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        InputFormatterDirective,
        ...CDK_IMPORTS,
        GenericDropdownComponent,
        LoadingButtonComponent,
        CustomMenuItemComponent
    ]
})
export class ModalManualStatusComponent
    extends ModalComponentBase<IModalManualStatusParameters, IManualStatusResponse>
    implements OnInit
{
    protected override parameters?: IModalManualStatusParameters;

    @ViewChildren(InputFormatterDirective) formatters!: QueryList<InputFormatterDirective>;

    isLoading: boolean;

    disabled: boolean;

    closeIcon: SafeHtml;

    isLoadingTemplates: boolean;

    opportunityStatus: OpportunityStatusView[];

    opportunityData: {
        actualValue?: number;
        actualGrossMargin?: number;
        opportunityStatus?: OpportunityStatusView;
    };

    actualGrossMargin?: number;
    errorActualValue?: string;

    opportunityManualId: boolean;

    opportunityId: number;

    constructor(
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly awaiter: AwaiterService,
        private readonly iconSanitizer: IconSanitizerService,
        private readonly opportunityStatusService: OpportunityStatusService,
        private readonly opportunityService: OpportunityService
    ) {
        super();
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
        this.isLoading = false;
        this.disabled = true;
        this.isLoadingTemplates = false;

        this.opportunityData = {
            actualValue: undefined,
            actualGrossMargin: undefined,
            opportunityStatus: undefined
        };
        this.actualGrossMargin = undefined;
        this.errorActualValue = undefined;
        this.opportunityStatus = [];
        this.opportunityManualId = false;
        this.opportunityId = 0;
    }

    async ngOnInit(): Promise<void> {
        this.opportunityManualId = !!this.parameters?.opportunityManualId;
        this.opportunityStatus = await this.opportunityStatusService.statusSearchManualStatus(
            !this.opportunityManualId
        );

        this.opportunityId = this.parameters?.opportunityId ?? 0;
        const selectedOpportunityStatus = this.opportunityStatus
            ? this.opportunityStatus.find((o) => o.id === this.parameters?.opportunityStatusId)
            : undefined;

        this.opportunityData.actualValue = this.parameters?.actualValue ?? 0;
        this.actualGrossMargin = this.parameters?.actualGrossMargin ? this.parameters?.actualGrossMargin * 100 : 0;
        this.opportunityData.actualGrossMargin = this.parameters?.actualGrossMargin ?? 0;
        this.opportunityData.opportunityStatus = selectedOpportunityStatus;
        this.changedDisabled();
    }

    manualStatusChange(selected: OpportunityStatusView): void {
        this.opportunityData.opportunityStatus = selected;
        this.opportunityData.actualValue = 0;
        this.opportunityData.actualGrossMargin = 0;
        this.actualGrossMargin = 0;
        this.changedDisabled();

        // Apply formatting to all inputs with libInputFormatter directive
        setTimeout(() => {
            this.formatters.forEach((formatter) => formatter.applyFormat());
        }, 0);
    }

    onGrossMarginChange(value: number) {
        const valueCheck = this.checkValue(value);
        if (valueCheck && valueCheck < 0) this.actualGrossMargin = 0;
        else if (valueCheck && valueCheck > 99.99) this.actualGrossMargin = 99.99;
        else this.actualGrossMargin = valueCheck;

        this.opportunityData.actualGrossMargin = this.actualGrossMargin
            ? this.actualGrossMargin / 100
            : this.actualGrossMargin;
        this.changedDisabled();
    }

    onActualValueEdited(value: any) {
        const valueCheck = this.checkValue(value);

        if (valueCheck && valueCheck > 99999999999999) {
            this.opportunityData.actualValue = valueCheck;
            this.errorActualValue = '*Input exceeds the maximum value of 99999999999999';
        } else {
            this.opportunityData.actualValue = valueCheck;
            this.errorActualValue = undefined;
        }

        this.changedDisabled();
    }

    checkValue(value: any): number | undefined {
        const valueN = Number(value);
        if (value === '' || value == null || isNaN(valueN)) {
            return undefined;
        }
        return valueN;
    }

    async onConfirm(): Promise<void> {
        await this.awaiter.awaitAction(
            'Manual Status...',
            async () => {
                try {
                    const result = await this.opportunityService.saveOpportunityManual({
                        actualValue: this.opportunityData.actualValue ?? undefined,
                        actualGrossMargin: this.opportunityData.actualGrossMargin ?? undefined,
                        opportunityId: this.opportunityId ?? undefined,
                        opportunityStatusId: this.opportunityData.opportunityStatus?.id ?? 0
                    });

                    this.snackbarNotificationService.success(`Manual Status has been updated successfully`);

                    this.close({
                        id: result?.id,
                        actualValue: result?.actualValue,
                        actualGrossMargin: result?.actualGrossMargin,
                        opportunityStatusId: this.opportunityData.opportunityStatus?.id
                    });
                } catch (error) {
                    const message = this.awaiter.processError(error);
                    this.snackbarNotificationService.error(message);
                }
            },
            (loading) => (this.isLoading = loading)
        );
    }

    private changedDisabled() {
        this.disabled =
            (this.opportunityData.opportunityStatus?.id != 1 && (this.checkValues() || !!this.errorActualValue)) ||
            !this.opportunityData.opportunityStatus?.id;
    }

    private checkValues() {
        return (
            this.opportunityData.actualValue == undefined ||
            this.opportunityData.actualValue == null ||
            this.opportunityData.actualGrossMargin == undefined ||
            this.opportunityData.actualGrossMargin == null
        );
    }
}
